/* HUMAN MATTER — fly-to-cart.
   Port of the BYQ gem `fly-to-cart-01` to vanilla JS. Every motion value below is the
   gem's, verbatim: 620 ms flight, quadratic Bezier with the control point 90 px above
   the higher endpoint, ease-in t = p², scale 1 → 0.35, opacity 1 until p = 0.82 then
   linear to 0; landing bump 0.42s cubic-bezier(.3,1.5,.5,1) and badge pop 0.4s
   cubic-bezier(.3,1.6,.5,1).

   Triggers: submit of any form[action*="/cart/add"], clicks on [data-quick-add], the
   sticky bar button. The flight is visual only and starts at the click; the spoken
   confirmation ("Added to cart") waits for the cart: Dawn's `cartUpdate` event from the
   product form (or the header's quick add) — cart-items publishes the same event after a
   quantity change or a removal, which is not an add — and is spoken once the bubble shows
   the new count. A refused add (cartError, 422, network) never announces success.
   Strings come from #elora-strings (Liquid `t`), so the copy translates with the theme. */

(function () {
  'use strict';

  if (window.EloraFlyToCart) return;

  var FLIGHT_DURATION = 620;
  var ARC_LIFT = 90;

  function reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  var strings = null;
  function str(key, fallback) {
    if (strings === null) {
      strings = {};
      var el = document.getElementById('elora-strings');
      if (el) {
        try { strings = JSON.parse(el.textContent) || {}; } catch (e) { strings = {}; }
      }
    }
    return strings[key] || fallback;
  }

  var stage = null;
  var live = null;
  var frames = [];
  var lastLanded = 0;
  var lastFlight = 0;
  var pendingAnnounce = false;
  var pendingTimer = null;
  var speakTimer = null;
  var bubbleWatcher = null;
  var ADD_SOURCES = { 'product-form': 1, 'elora-header': 1 };

  function intend() {
    pendingAnnounce = true;
    window.clearTimeout(pendingTimer);
    pendingTimer = window.setTimeout(function () { pendingAnnounce = false; }, 20000);
  }

  function getStage() {
    if (stage && stage.isConnected) return stage;
    stage = document.createElement('div');
    stage.className = 'hmh-ftc-stage';
    stage.setAttribute('aria-hidden', 'true');
    document.body.appendChild(stage);
    return stage;
  }

  function getLive() {
    if (live && live.isConnected) return live;
    live = document.createElement('span');
    live.className = 'hmh-ftc-live visually-hidden';
    live.setAttribute('aria-live', 'polite');
    document.body.appendChild(live);
    return live;
  }

  function cartEl() {
    return document.getElementById('cart-icon-bubble');
  }

  function cartCount() {
    var el = cartEl();
    if (!el) return 0;
    var badge = el.querySelector('.cart-count-bubble [aria-hidden="true"]');
    return badge ? parseInt(badge.textContent.trim(), 10) || 0 : 0;
  }

  /* the stage is position: fixed at inset 0, so viewport coordinates are stage
     coordinates — the arc stays correct however far the page is scrolled */
  function centre(el) {
    var r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }

  function onScreen(el) {
    if (!el) return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function replay(el, cls) {
    if (!el) return;
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
    window.setTimeout(function () {
      el.classList.remove(cls);
    }, 700);
  }

  function announce() {
    /* the bubble already carries Dawn's translated, pluralised "N items" for screen readers */
    var el = cartEl();
    var hidden = el && el.querySelector('.cart-count-bubble .visually-hidden');
    var count = hidden ? hidden.textContent.trim() : '';
    getLive().textContent = str('added', 'Added to cart.') + (count ? ' ' + count : '');
  }

  function bump(silent) {
    var cart = cartEl();
    if (!cart || reduced() || silent) return;
    replay(cart, 'hmh-ftc-bump');
    replay(cart.querySelector('.cart-count-bubble'), 'hmh-ftc-pop');
    lastLanded = Date.now();
  }

  function fly(thumb, origin) {
    /* Noom LP (12.09): the drawer opens on the click itself (cart-drawer[data-instant-open], hm-cart-drawer.js) and
       covers the bag — a flight would land behind it, so there is none; the spoken confirmation below still runs */
    if (document.querySelector('cart-drawer[data-instant-open]')) return;
    // a tile can be both [data-quick-add] and a submit button inside an add-to-cart
    // form (the cart lane's recommendations are): the click fires first and wins
    if (Date.now() - lastFlight < 200) return;
    lastFlight = Date.now();

    var cart = cartEl();
    /* a thumb that is hidden on this breakpoint (the sticky bar's on phones) has a 0×0 box:
       take off from the pressed control instead of the page corner */
    var from = onScreen(thumb) ? thumb : (onScreen(origin) ? origin : null);
    if (!cart || !from || reduced()) return;

    var s = centre(from);
    var e = centre(cart);
    var c = { x: (s.x + e.x) / 2, y: Math.min(s.y, e.y) - ARC_LIFT };

    var dot = document.createElement('span');
    dot.className = 'hmh-ftc-dot';
    var img = thumb && thumb.tagName === 'IMG' ? thumb : null;
    var src = img ? (img.currentSrc || img.src) : '';
    if (src) dot.style.backgroundImage = 'url("' + src + '")';
    getStage().appendChild(dot);

    var start = null;

    function step(now) {
      if (start === null) start = now;
      var p = Math.min((now - start) / FLIGHT_DURATION, 1);
      var t = p * p;
      var mt = 1 - t;
      var x = mt * mt * s.x + 2 * mt * t * c.x + t * t * e.x;
      var y = mt * mt * s.y + 2 * mt * t * c.y + t * t * e.y;
      var scale = 1 - 0.65 * t;
      var opacity = p < 0.82 ? 1 : 1 - (p - 0.82) / 0.18;
      dot.style.transform = 'translate(' + x + 'px, ' + y + 'px) scale(' + scale + ')';
      dot.style.opacity = String(Math.max(0, opacity));
      if (p < 1) {
        frames.push(requestAnimationFrame(step));
      } else {
        dot.remove();
        frames = [];
        bump(Date.now() - lastLanded < 1200);
      }
    }

    frames.push(requestAnimationFrame(step));
  }

  /* ------------------------------------------------------------- thumb lookup */

  function firstImage(root) {
    if (!root) return null;
    var marked = root.querySelector('[data-fly-thumb] img, img[data-fly-thumb]');
    if (marked) return marked;
    return root.querySelector('img');
  }

  function galleryImage() {
    // the gallery is a measured track: take the slide under the stage
    var stageEl = document.querySelector('.pdp__stage');
    if (stageEl) {
      var slides = stageEl.querySelectorAll('.pdp__slide');
      var box = stageEl.getBoundingClientRect();
      for (var i = 0; i < slides.length; i += 1) {
        var r = slides[i].getBoundingClientRect();
        if (r.right > box.left + 8 && r.left < box.right - 8) {
          var img = slides[i].querySelector('img');
          if (img) return img;
        }
      }
      var fallback = stageEl.querySelector('img');
      if (fallback) return fallback;
    }
    return firstImage(document.querySelector('.product, main'));
  }

  function thumbForForm(form) {
    var scoped = form.closest('li, .card, .e-rec__item, .pdp, product-info, .product') || form;
    var marked = scoped.querySelector('[data-fly-thumb] img, img[data-fly-thumb]');
    if (marked) return marked;
    if (document.querySelector('.pdp__stage') && form.closest('.pdp')) return galleryImage();
    return firstImage(scoped) || galleryImage();
  }

  /* ---------------------------------------------------------------- triggers */

  // (a) any add-to-cart form — capture, so it fires before the AJAX round trip
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
      intend();
      fly(thumbForForm(form), form.querySelector('[type="submit"]'));
    },
    true
  );

  // (b) quick-add buttons: the header's mega tiles, the cart drawer's "You might like",
  //     product cards — anything marked [data-quick-add]
  document.addEventListener(
    'click',
    function (event) {
      var btn = event.target.closest ? event.target.closest('[data-quick-add]') : null;
      if (btn) {
        var tile = btn.closest('li, .card, .hmh__tile, .e-rec__item, form') || btn.parentElement;
        intend();
        fly(firstImage(tile), btn);
        return;
      }
      // (c) the sticky buy bar
      var sbar = event.target.closest ? event.target.closest('[data-elora-sbar-btn], .sbar__btn') : null;
      if (sbar && !sbar.disabled) {
        intend();
        fly(document.querySelector('.sbar__thumb img') || galleryImage(), sbar);
      }
    },
    true
  );

  // (d) the cart confirms an add: only the product form's / the header's cartUpdate counts; the count is spoken
  //     once the bubble has re-rendered (Dawn publishes before it renders), or after 1.2 s at the latest
  function speak() {
    window.clearTimeout(speakTimer);
    speakTimer = null;
    announce();
  }
  function onCartUpdate(event) {
    if (!pendingAnnounce || !event || !ADD_SOURCES[event.source]) return;
    pendingAnnounce = false;
    window.clearTimeout(pendingTimer);
    window.clearTimeout(speakTimer);
    speakTimer = window.setTimeout(speak, 1200);
    if (reduced() || !document.querySelector('.hmh-ftc-dot')) bump(Date.now() - lastLanded < 1200);
  }
  function onCartError() {
    pendingAnnounce = false;
    window.clearTimeout(pendingTimer);
  }
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
    if (PUB_SUB_EVENTS.cartError) subscribe(PUB_SUB_EVENTS.cartError, onCartError);
  }

  // (e) the bubble re-renders with the real count — that is the moment to speak (re-armed when the header section reloads)
  function watchBubble() {
    var el = cartEl();
    if (bubbleWatcher) { bubbleWatcher.disconnect(); bubbleWatcher = null; }
    if (!el || !window.MutationObserver) return;
    var last = cartCount();
    bubbleWatcher = new MutationObserver(function () {
      var next = cartCount();
      if (next === last) return;
      last = next;
      if (speakTimer) speak();
    });
    bubbleWatcher.observe(el, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchBubble);
  } else {
    watchBubble();
  }
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('#cart-icon-bubble')) watchBubble();
  });

  window.addEventListener('pagehide', function () {
    frames.forEach(function (id) {
      cancelAnimationFrame(id);
    });
    frames = [];
  });

  window.EloraFlyToCart = { fly: fly, announce: announce };
})();
